"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { createPlan, updatePlan } from "../../../services/billingService";
import { rupeesToPaise } from "../../../utils/billingFormat";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  name: "",
  description: "",
  baseAmount: "",
  durationMonths: "3",
  maxUsers: "",
  gstRate: "18",
};

const PlanFormModal = ({ isOpen, onClose, onSaved, plan }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const isEditing = Boolean(plan);

  useEffect(() => {
    if (!isOpen) return;
    if (plan) {
      setFormData({
        name: plan.name || "",
        description: plan.description || "",
        baseAmount: plan.baseAmount != null ? String(plan.baseAmount / 100) : "",
        durationMonths: String(plan.durationMonths || 3),
        maxUsers: plan.maxUsers != null ? String(plan.maxUsers) : "",
        gstRate: plan.gstRate != null ? String(plan.gstRate) : "18",
      });
    } else {
      setFormData(emptyForm);
    }
  }, [isOpen, plan]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        baseAmount: rupeesToPaise(formData.baseAmount),
        durationMonths: Number(formData.durationMonths),
        maxUsers: Number(formData.maxUsers),
        gstRate: Number(formData.gstRate),
      };
      if (isEditing) {
        await updatePlan(plan._id, payload);
        toast.success("Plan updated");
      } else {
        await createPlan(payload);
        toast.success("Plan created");
      }
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Plan" : "Create Plan"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Plan Name *</Label>
            <Input name="name" value={formData.name} onChange={handleChange} required placeholder="e.g. Business" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Optional" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Price (₹, GST excl.) *</Label>
              <Input name="baseAmount" type="number" min="0" step="0.01" value={formData.baseAmount} onChange={handleChange} required placeholder="10000" />
            </div>
            <div>
              <Label>GST Rate (%) *</Label>
              <Input name="gstRate" type="number" min="0" max="100" value={formData.gstRate} onChange={handleChange} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Duration *</Label>
              <Select value={formData.durationMonths} onValueChange={(v) => setFormData((prev) => ({ ...prev, durationMonths: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Users *</Label>
              <Input name="maxUsers" type="number" min="1" value={formData.maxUsers} onChange={handleChange} required placeholder="50" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : isEditing ? "Save Changes" : "Create Plan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PlanFormModal;
